import { TextOverflowTooltip } from '@ipfabric/ui-components/Tooltip/TextOverflowTooltip';
import classNames from 'classnames';
import { ButtonAsA } from 'components/Button/ButtonAsA';
import { isHighlightedChainItem } from 'components/SecuritySchema/helpers/isHighlightedChainItem';
import type {
  PolicyNode,
  SecuritySchemaNodeComponentProps,
} from 'components/SecuritySchema/model/SecuritySchemaNode';
import { navigateToRuleChain } from 'components/SecuritySchema/model/commands/navigateToRuleChain';
import type { FC } from 'react';

/** How each cloud policy kind is called in its provider's console. */
const policyKindLabel: { [Kind in PolicyNode['policy']['kind']]: string } = {
  gcpFirewallPolicy: 'Firewall policy',
  gcpVpcFirewall: 'VPC firewall rules',
  nacl: 'Network ACL',
  nsg: 'Network security group',
  securityGroup: 'Security group',
};

/**
 * The box a cloud policy's rules hang off — what tells the reader which security
 * group / NACL / NSG / firewall policy a rule came from, since an enforcement
 * point evaluates all of them. With `chainName` the rules are a chain of their
 * own rather than children, so the box links into them and is itself a chain
 * item, highlighted when the decision passed through this policy.
 */
export const CPolicyNode: FC<SecuritySchemaNodeComponentProps<PolicyNode>> = ({
  context,
  node,
}) => {
  const { chainName } = node;
  return (
    <div
      className={classNames(
        'security-tree-node security-tree-node--policy',
        isHighlightedChainItem(node, context) &&
          'security-tree-node--highlighted',
      )}
      // See `CRuleNode` for why the tree is navigated by data attributes.
      data-security-rule-index={node.pageIndex}
    >
      <TextOverflowTooltip as='p' className='security-tree-node__heading'>
        {node.policy.name ?? node.policy.id}
      </TextOverflowTooltip>
      <p>{policyKindLabel[node.policy.kind]}</p>
      {chainName !== undefined && (
        <p>
          <ButtonAsA
            onClick={() => {
              context.dispatch(navigateToRuleChain, {
                chainName,
                securityType: context.securityType,
                treeScrollContainer: context.treeScrollContainerRef.current,
              });
            }}
          >
            Show rules
          </ButtonAsA>
        </p>
      )}
    </div>
  );
};
